import { realtimeManager } from '@/lib/manager realtime/realtimeManager';
import { useEffect, useState, useRef, useCallback, useMemo } from 'react';

export function useRealtimeCollection<T>(
  databaseId: string,
  collectionIds: string | string[],
  fetcher: () => Promise<T[]>,
  filterEvents: string[] = ['create', 'update', 'delete']
): [T[], boolean, () => void] {
  const [items, setItems] = useState<T[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [_error, setError] = useState<Error | null>(null);
  const abortControllerRef = useRef<AbortController | null>(null);
  const isUnmountedRef = useRef(false);
  const subscriptionIdRef = useRef<string | null>(null);
  const lastFetchRef = useRef<number>(0);

  // Stabilize dependencies with useMemo
  const stableCollectionIds = useMemo(() => 
    Array.isArray(collectionIds) ? collectionIds : [collectionIds], 
    [collectionIds]
  );
  
  const stableFilterEvents = useMemo(() => filterEvents, [filterEvents.join(',')]);

  // Memoize channels to prevent unnecessary re-renders
  const channels = useMemo(() => {
    return stableCollectionIds.map(id => 
      `databases.${databaseId}.collections.${id}.documents`
    );
  }, [databaseId, stableCollectionIds]);

  // Improved refresh function with debouncing
  const refreshData = useCallback(async (showLoading = true) => {
    if (isUnmountedRef.current) return;
    
    const now = Date.now();
    lastFetchRef.current = now;
    
    // Cancel previous request
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }
    
    const controller = new AbortController();
    abortControllerRef.current = controller;
    
    if (showLoading) {
      setIsLoading(true);
    }
    setError(null);
    
    try {
      const fetchPromise = fetcher();
      const timeoutPromise = new Promise<never>((_, reject) => 
        setTimeout(() => reject(new Error('Request timeout')), 15000)
      );
      
      const fresh = await Promise.race([fetchPromise, timeoutPromise]);
      
      // Check if this is still the latest request
      if (!controller.signal.aborted && 
          !isUnmountedRef.current && 
          lastFetchRef.current === now) {
        setItems(fresh);
        setError(null);
      }
    } catch (err: any) {
      if (!controller.signal.aborted && 
          !isUnmountedRef.current && 
          lastFetchRef.current === now) {
        const errorMessage = err?.message || 'Error desconocido';
        
        // Only set error if no previous data exists
        if (items.length === 0) {
          setError(new Error(errorMessage));
        }
      }
    } finally {
      if (!controller.signal.aborted && 
          !isUnmountedRef.current && 
          lastFetchRef.current === now) {
        setIsLoading(false);
      }
    }
  }, [fetcher]);

  // Improved realtime event handler with debouncing
  const handleRealtimeEvent = useCallback((response: any) => {
    if (isUnmountedRef.current) return;
    
    try {
      const hasRelevantEvent = response.events?.some((event: string) =>
        stableFilterEvents.some(type => event.includes(type))
      );

      if (hasRelevantEvent) {
        
        // Debounce rapid events to avoid excessive API calls
        setTimeout(() => {
          if (!isUnmountedRef.current) {
            refreshData(false);
          }
        }, 100);
      }
    } catch (error) {
    }
  }, [stableFilterEvents, refreshData]);

  // Effect for subscription and initial data loading
  useEffect(() => {
    isUnmountedRef.current = false;
    
    // Load initial data
    refreshData(true);

    // Subscribe to realtime
    const subscribeToRealtime = async () => {
      try {
        // Unsubscribe from previous subscription if exists
        if (subscriptionIdRef.current) {
          realtimeManager.unsubscribe(subscriptionIdRef.current);
          subscriptionIdRef.current = null;
        }

        const subscriptionId = await realtimeManager.subscribe(channels, handleRealtimeEvent);
        subscriptionIdRef.current = subscriptionId;
      } catch (error) {
        // Continue without realtime if it fails
      }
    };

    // Small delay to ensure connection is stable
    const timeoutId = setTimeout(subscribeToRealtime, 100);

    // Cleanup function
    return () => {
      isUnmountedRef.current = true;
      clearTimeout(timeoutId);
      
      // Cancel pending requests
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
        abortControllerRef.current = null;
      }
      
      // Unsubscribe from realtime
      if (subscriptionIdRef.current) {
        realtimeManager.unsubscribe(subscriptionIdRef.current);
        subscriptionIdRef.current = null;
      }
    };
  }, [databaseId, channels.join(','), handleRealtimeEvent]);

  // Manual refresh function
  const manualRefresh = useCallback(() => refreshData(true), [refreshData]);

  return [items, isLoading, manualRefresh];
}